import { z } from 'zod';
import { centAmount, isoDate } from './posting.js';
/**
 * Die Verträge der Stammdaten — Kunden, Kreditoren, Verknüpfungen,
 * USt-IdNr.-Prüfungen, Import.
 *
 * **Was hier geschieht, ist Normalisierung und nicht Durchsetzung.** Die Regeln
 * stehen als `CHECK` in den Migrationen 0011 bis 0017 und gelten auch gegen
 * direktes SQL; was hier steht, bringt die Eingabe in die Form, die die
 * Datenbank erwartet, und liefert eine verständliche Meldung, bevor die
 * Datenbank eine unverständliche liefert. Dieselbe Doktrin wie in Phase 1
 * (Spec 6.5).
 *
 * **Jede Normalisierung steht in einer `.transform()` mit `ctx` und `z.NEVER`,
 * niemals in einer `.superRefine()`.** Deren Rückgabewert ist als `void`
 * typisiert und wird zur Laufzeit verworfen: Ein damit gebautes Schema gäbe die
 * **rohe** Eingabe zurück, `'de 116 639 855'` bliebe `'de 116 639 855'` und
 * ginge unkanonisiert in die Datenbank, obwohl die Prüfung grün war.
 */
// --- Bausteine der Normalisierung ----------------------------------------
/**
 * Die geschlossene Liste der Anreden.
 *
 * Geschlossen und nicht heuristisch: `Dr.` und `Prof.` bleiben im Namen, weil
 * sie Namensbestandteil und keine Anrede sind (Spec 5.8). Eine Heuristik über
 * „kurzes erstes Wort mit Punkt" träfe beide und wäre bei jedem neuen Titel
 * wieder falsch.
 */
const ANREDEN = [
    'Herr',
    'Herrn',
    'Frau',
    'Monsieur',
    'Madame',
    'Mevrouw',
    'Mijnheer',
    'Mr',
    'Mrs',
    'Ms',
];
/** Trimmt und zieht Folgen von Leerraum auf ein Leerzeichen zusammen. */
function straffen(wert) {
    return wert.trim().replace(/\s+/g, ' ');
}
/**
 * Ein optionales Textfeld: getrimmt, Leerraumfolgen zusammengezogen, und was
 * danach leer ist, wird `NULL`.
 *
 * Die letzte Regel gilt DURCHGÄNGIG und nicht feldweise, weil eine feldweise
 * Aufzählung genau die Stelle vergisst, die später dazukommt (Spec 5.8).
 */
const optionalText = z
    .string()
    .optional()
    .nullable()
    .transform((wert) => {
    if (wert === undefined || wert === null)
        return null;
    const gestrafft = straffen(wert);
    return gestrafft === '' ? null : gestrafft;
});
/**
 * Dasselbe für einen Änderungssatz — mit einem dritten Zustand.
 *
 * Im Patch heisst ein **fehlendes** Feld „nicht ändern" und ein ausdrückliches
 * `null` „leeren". `optionalText` kennt den Unterschied nicht: Es macht aus
 * beidem `null`, und ein `PATCH { city: 'Bremen' }` löschte damit die
 * Telefonnummer. Deshalb bleibt `undefined` hier `undefined`.
 */
const optionalPatchText = z
    .union([z.string(), z.null()])
    .optional()
    .transform((wert) => {
    if (wert === undefined)
        return undefined;
    if (wert === null)
        return null;
    const gestrafft = straffen(wert);
    return gestrafft === '' ? null : gestrafft;
});
/** Ein Pflichttextfeld. Leer heisst Ablehnung, nicht Platzhalter. */
const pflichtText = (feld) => z.string().transform((wert, ctx) => {
    const gestrafft = straffen(wert);
    if (gestrafft === '') {
        ctx.addIssue({ code: z.ZodIssueCode.custom, message: `${feld} darf nicht leer sein` });
        return z.NEVER;
    }
    return gestrafft;
});
/** Dasselbe als Patch-Feld: fehlt es, bleibt es; steht es da, darf es nicht leer sein. */
const pflichtPatchText = (feld) => z
    .string()
    .optional()
    .transform((wert, ctx) => {
    if (wert === undefined)
        return undefined;
    const gestrafft = straffen(wert);
    if (gestrafft === '') {
        ctx.addIssue({ code: z.ZodIssueCode.custom, message: `${feld} darf nicht leer sein` });
        return z.NEVER;
    }
    return gestrafft;
});
/**
 * Die kanonische Form einer USt-IdNr.: Grossbuchstaben, nur `A-Z0-9`.
 *
 * **Exportiert, weil der Import sie ausserhalb eines Schemas braucht.** Er
 * vergleicht die USt-IdNr. JEDER Kopie einer Kundennummer gegen die des
 * Rechnungsstellers (Fall E-169) — auch die der Kopien, die er nicht übernimmt
 * und deshalb nicht durch `customerImportRowSchema` schickt. Der Altbestand
 * schreibt sie mit Leerzeichen (`'DE 350652563'`), und ein zweiter Nachbau
 * dieser Regel wäre genau die Art Abweichung, die man erst am falschen Ergebnis
 * bemerkt.
 */
export function canonicalVatId(wert) {
    return wert.toUpperCase().replace(/[^A-Z0-9]/g, '');
}
/** Die kanonische Form einer IBAN: Grossbuchstaben, kein Leerraum. */
function kanonischeIban(wert) {
    return wert.toUpperCase().replace(/\s+/g, '');
}
/** Die Aussenform einer IBAN nach ISO 13616 — Landeskürzel, zwei Prüfziffern, bis zu 30 alphanumerische Zeichen. */
const IBAN_FORM = /^[A-Z]{2}[0-9]{2}[A-Z0-9]{11,30}$/;
/**
 * Eine IBAN, wie ein Nutzer sie eingibt — **EIN** Schema für jede Eingabestelle
 * (Auftrag `LS-ibaneingabe`): Mandat, Kreditor, Aussteller-Bankkonto,
 * Bankkonto. Leerzeichen und Kleinschreibung werden bereinigt — die
 * Gruppenschreibweise, in der Banken eine IBAN drucken
 * (`DE06 7415 0000 0420 6359 48`), wird angenommen und nicht abgewiesen —,
 * geprüft wird danach nur die AUSSENFORM.
 *
 * **Die Prüfziffer bleibt Sache der Datenbank** (`iban_wellformed_v1`,
 * Migration 0011 — eine Rechnung, ein Ort). Wer sie zusätzlich am Vertrag
 * halten will, hängt eine weitere `.transform()` an, die {@link ibanWellformed}
 * ruft — so wie {@link ibanMitPruefzifferSchema} es tut, das `optionalIban`/
 * `optionalPatchIban` unten für den Kreditor benutzen und das der
 * Rechnungssteller für seine Bankverbindungen benutzt.
 */
export const ibanSchema = z.string().transform((wert, ctx) => {
    const kanonisch = kanonischeIban(wert);
    if (!IBAN_FORM.test(kanonisch)) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            params: { code: 'IBAN_INVALID' },
            message: 'IBAN hat eine unzulässige Form. Eine IBAN beginnt mit zwei Buchstaben und zwei Prüfziffern; ' +
                'eine deutsche hat 22 Zeichen.',
        });
        return z.NEVER;
    }
    return kanonisch;
});
/**
 * Dieselbe Eingabe, zusätzlich mit Prüfsummenprüfung am Vertrag — für Stellen,
 * die bislang schon die volle Prüfung hielten (Kreditor, Aussteller) und diese
 * Strenge mit der Vereinheitlichung nicht verlieren sollen.
 */
export const ibanMitPruefzifferSchema = ibanSchema.transform((wert, ctx) => {
    if (!ibanWellformed(wert)) {
        // Die häufigste Ursache eines Tippfehlers ist die Länge: Eine deutsche IBAN hat
        // genau 22 Zeichen, und wer eines verliert, bekommt sonst nur «falsche Prüfsumme».
        const deutscheLaenge = wert.startsWith('DE') && wert.length !== 22;
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            params: { code: 'IBAN_INVALID' },
            message: deutscheLaenge
                ? `Eine deutsche IBAN hat 22 Zeichen, diese hat ${String(wert.length)}. Bitte noch einmal prüfen.`
                : 'IBAN hat eine unzulässige Form oder eine falsche Prüfsumme. Bitte noch einmal prüfen.',
        });
        return z.NEVER;
    }
    return wert;
});
/**
 * USt-IdNr.: Grossbuchstaben, alles ausser `A-Z0-9` entfernen, leer wird
 * `NULL`, eine unzulässige Form wird abgelehnt.
 */
const optionalVatId = z
    .string()
    .optional()
    .nullable()
    .transform((wert, ctx) => {
    if (wert === undefined || wert === null)
        return null;
    const kanonisch = canonicalVatId(wert);
    if (kanonisch === '')
        return null;
    if (!vatIdWellformed(kanonisch)) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            params: { code: 'VAT_ID_INVALID' },
            message: 'USt-IdNr. hat eine unzulässige Form oder eine falsche Prüfziffer',
        });
        return z.NEVER;
    }
    return kanonisch;
});
/** Dieselbe Prüfung als Patch-Feld. `null` heisst „USt-IdNr. entfernen". */
const optionalPatchVatId = z
    .union([z.string(), z.null()])
    .optional()
    .transform((wert, ctx) => {
    if (wert === undefined)
        return undefined;
    if (wert === null)
        return null;
    const kanonisch = canonicalVatId(wert);
    if (kanonisch === '')
        return null;
    if (!vatIdWellformed(kanonisch)) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            params: { code: 'VAT_ID_INVALID' },
            message: 'USt-IdNr. hat eine unzulässige Form oder eine falsche Prüfziffer',
        });
        return z.NEVER;
    }
    return kanonisch;
});
/**
 * USt-IdNr. **ohne** Formprüfung — nur die Kanonisierung.
 *
 * Ausschliesslich für den Import: Dort ist ein nicht leerer, aber ungültiger
 * Wert eine **Warnung** und kein Grund, den Kunden nicht zu führen (Spec 6.6
 * Schritt 7). Die Prüfung geschieht dort im Dienst, weil erst er den Satz
 * anlegen und zugleich warnen kann.
 */
const importVatId = z
    .string()
    .optional()
    .nullable()
    .transform((wert) => {
    if (wert === undefined || wert === null)
        return null;
    const kanonisch = canonicalVatId(wert);
    return kanonisch === '' ? null : kanonisch;
});
/**
 * IBAN: Grossbuchstaben ohne Leerraum, leer wird `NULL`, falsche Prüfsumme
 * wird abgelehnt.
 *
 * Normalisierung und Formprüfung kommen aus {@link ibanMitPruefzifferSchema}
 * (Auftrag `LS-ibaneingabe`) — nur die Optional/Patch-Hülle steht hier noch.
 */
const optionalIban = z
    .string()
    .optional()
    .nullable()
    .transform((wert, ctx) => {
    if (wert === undefined || wert === null)
        return null;
    if (wert.trim() === '')
        return null;
    const ergebnis = ibanMitPruefzifferSchema.safeParse(wert);
    if (!ergebnis.success) {
        for (const issue of ergebnis.error.issues)
            ctx.addIssue(issue);
        return z.NEVER;
    }
    return ergebnis.data;
});
/** Dieselbe Prüfung als Patch-Feld. */
const optionalPatchIban = z
    .union([z.string(), z.null()])
    .optional()
    .transform((wert, ctx) => {
    if (wert === undefined)
        return undefined;
    if (wert === null)
        return null;
    if (wert.trim() === '')
        return null;
    const ergebnis = ibanMitPruefzifferSchema.safeParse(wert);
    if (!ergebnis.success) {
        for (const issue of ergebnis.error.issues)
            ctx.addIssue(issue);
        return z.NEVER;
    }
    return ergebnis.data;
});
/**
 * E-Mail: getrimmt und kleingeschrieben, leer wird `NULL`.
 *
 * **Keine Formprüfung hier.** Die Spalte ist `citext`, und die Form setzt
 * `customer_email_shape` beziehungsweise `vendor_email_shape` durch. Eine
 * zweite Prüfung an dieser Stelle nähme dem Constraint seinen einzigen
 * erreichbaren Weg — den Import, dessen Altbestand im E-Mail-Feld führen kann,
 * was er will (Spec 6.11) — und der Code `EMAIL_INVALID` hätte keinen Auslöser.
 */
const optionalEmail = z
    .string()
    .optional()
    .nullable()
    .transform((wert) => {
    if (wert === undefined || wert === null)
        return null;
    const gestrafft = wert.trim().toLowerCase();
    return gestrafft === '' ? null : gestrafft;
});
/** Dieselbe Umsetzung als Patch-Feld. */
const optionalPatchEmail = z
    .union([z.string(), z.null()])
    .optional()
    .transform((wert) => {
    if (wert === undefined)
        return undefined;
    if (wert === null)
        return null;
    const gestrafft = wert.trim().toLowerCase();
    return gestrafft === '' ? null : gestrafft;
});
/**
 * Das Steuerregime — die sechs Werte aus `0043_eingangsbelege_typen.sql`.
 *
 * **Hier definiert und nicht in `eingangsrechnungen.ts`**, aus demselben
 * Grund wie bei `vatCheckMethodSchema` weiter unten: `vendor.default_tax_regime`
 * (Kreditor, diese Datei) und `incoming_invoice_tax_line.tax_regime`
 * (`eingangsrechnungen.ts`) sind DIESELBE Aufzählung — zwei Abschriften
 * wären zwei Gelegenheiten, dass ein siebter Wert nur an einer davon
 * ankommt. `eingangsrechnungen.ts` importiert sie deshalb von hier, wie es
 * `vatCheckMethodSchema`/`vatCheckResultSchema` bereits vormachen. Vor den
 * `optional*`-Textfeldern platziert, weil `optionalTaxRegime` unten sie
 * sofort braucht — TypeScript kennt hier kein Hoisting über `const`.
 */
export const incomingTaxRegimeSchema = z.enum([
    'regular',
    'rc_13b_abs1',
    'rc_13b_abs2_nr1',
    'rc_13b_abs2_nr12',
    'not_taxable',
    'tax_exempt',
]);
/**
 * Das Steuerregime-VORSCHLAGSFELD eines Kreditors (`vendor.default_tax_regime`,
 * `0043`). Fehlt es oder ist es leer, wird `NULL` — „kein Vorschlag". Die
 * leere Zeichenkette ist ausdrücklich Teil der Union: Ein `<select>` ohne
 * Auswahl in `VendorForm.tsx` sendet `''` und keinen `undefined`-Wert,
 * dieselbe Lage wie bei `optionalText` und einem leeren Textfeld.
 */
const optionalTaxRegime = z
    .union([incomingTaxRegimeSchema, z.literal('')])
    .optional()
    .nullable()
    .transform((wert) => {
    if (wert === undefined || wert === null || wert === '')
        return null;
    return wert;
});
/**
 * Dasselbe als Patch-Feld: fehlt es, bleibt es; `null` oder `''` heisst
 * „Vorschlag entfernen" — dieselbe Lesart wie bei `optionalPatchText`, denn
 * die Änderungsmaske in `VendorPage.tsx` ist mit dem aktuellen Wert
 * vorbelegt und sendet bei jeder Änderung ein `<select>`-Ergebnis, nie
 * `undefined`.
 */
const optionalPatchTaxRegime = z
    .union([incomingTaxRegimeSchema, z.literal(''), z.null()])
    .optional()
    .transform((wert) => {
    if (wert === undefined)
        return undefined;
    if (wert === null || wert === '')
        return null;
    return wert;
});
/** Eine UUID, wie sie Kunden, Kreditoren und Verknüpfungen identifiziert. */
const uuid = z.string().uuid('Keine gültige Kennung');
/**
 * Trennt eine führende Anrede aus der geschlossenen Liste ab.
 *
 * Liefert `null` als Anrede, wenn keine gefunden wurde, und `null` als Namen,
 * wenn nach dem Abtrennen nichts übrig bleibt — der Aufrufer lehnt den Satz
 * dann ab (Spec 5.8).
 */
export function splitSalutation(name) {
    const gestrafft = straffen(name);
    const trennung = gestrafft.indexOf(' ');
    const erstes = trennung < 0 ? gestrafft : gestrafft.slice(0, trennung);
    // Ein nachgestellter Punkt gehört zur Schreibweise und nicht zum Wort:
    // 'Mr. Smith' und 'Mr Smith' sind dieselbe Anrede.
    const ohnePunkt = erstes.endsWith('.') ? erstes.slice(0, -1) : erstes;
    const treffer = ANREDEN.find((a) => a.toLowerCase() === ohnePunkt.toLowerCase());
    if (treffer === undefined)
        return { salutation: null, name: gestrafft === '' ? null : gestrafft };
    const rest = trennung < 0 ? '' : straffen(gestrafft.slice(trennung + 1));
    return { salutation: treffer, name: rest === '' ? null : rest };
}
// --- Nachbauten der beiden SQL-Prüffunktionen ----------------------------
/**
 * Form der USt-IdNr. plus deutsche Prüfziffer — der TypeScript-Nachbau von
 * `vat_id_wellformed_v1` aus Migration 0011.
 *
 * Erwartet den bereits kanonisierten Wert. Er dient der verständlichen
 * Meldung; durchgesetzt wird die Regel weiterhin vom `CHECK`. Ein Test in
 * `test/master-data.test.ts` vergleicht ihn gegen dieselben Eingaben wie der
 * Durchsetzungstest der Migration, damit beide nicht auseinanderlaufen.
 *
 * Für Nicht-DE-Präfixe wird NUR die Form geprüft — die Prüfzifferverfahren der
 * übrigen Mitgliedstaaten sind verschieden, teils undokumentiert und teils gar
 * nicht vorhanden.
 */
export function vatIdWellformed(value) {
    if (!/^[A-Z]{2}[A-Z0-9]{2,12}$/.test(value))
        return false;
    if (!value.startsWith('DE'))
        return true;
    const ziffern = value.slice(2);
    if (!/^[0-9]{9}$/.test(ziffern))
        return false;
    // ISO 7064, MOD 11,10 — zeichengleich mit der PL/pgSQL-Fassung.
    let v = 10;
    for (let i = 0; i < 8; i += 1) {
        let s = (v + Number(ziffern[i])) % 10;
        if (s === 0)
            s = 10;
        v = (2 * s) % 11;
    }
    let d = 11 - v;
    if (d === 10)
        d = 0;
    return d === Number(ziffern[8]);
}
/**
 * IBAN-Prüfsumme nach ISO 7064 MOD 97-10 — der Nachbau von
 * `iban_wellformed_v1` aus Migration 0011.
 *
 * Gerechnet wird stückweise über den Rest und nicht über eine einzige Zahl:
 * Die umgestellte IBAN hat bis zu 68 Stellen und passt in keinen sicheren
 * `number`. Dieselbe Begründung wie in der SQL-Fassung, dort für `bigint`.
 */
export function ibanWellformed(value) {
    if (!/^[A-Z]{2}[0-9]{2}[A-Z0-9]{11,30}$/.test(value))
        return false;
    const umgestellt = value.slice(4) + value.slice(0, 4);
    let rest = 0;
    for (const zeichen of umgestellt) {
        const teil = /[0-9]/.test(zeichen)
            ? zeichen
            : String(zeichen.charCodeAt(0) - 55);
        for (const ziffer of teil)
            rest = (rest * 10 + Number(ziffer)) % 97;
    }
    return rest === 1;
}
// --- Kunden ---------------------------------------------------------------
/**
 * Woher ein Kundensatz stammt — deckungsgleich mit dem Aufzählungstyp
 * `customer_origin` (`0011`, erweitert um `altsystem` in `0120`).
 *
 * | Wert | Bedeutung |
 * |---|---|
 * | `famesystems` | aus dem FameSystems-Webinterface, vier- und fünfstellige Nummern |
 * | `manual` | im neuen System von Hand angelegt |
 * | `altsystem` | aus dem eigenen Stamm des Altsystems, sechsstellige Nummern (`D-11`) |
 * | `vorsystem` | aus dem Stamm eines Vorsystems (Lexware Office), Migration `0250`; welches, sagt `source_system` |
 *
 * **`altsystem` steht hier, weil `customerViewSchema` sonst an genau den 359
 * Kunden zerbräche, für die es den Wert gibt.** Die Ansichtsform ist die
 * Antwortform von `GET /customers`; ein Wert, den die Datenbank führt und der
 * Vertrag nicht kennt, macht aus einem gelesenen Kunden einen Zod-Fehler — und
 * zwar erst dann, wenn der Import gelaufen ist. Aus demselben Grund steht
 * `vorsystem` hier, bevor der erste Stammdatenlauf ihn vergibt (`E-1570`).
 *
 * **Anlegen darf keinen der beiden jemand**, siehe `customerAnlageOriginSchema`.
 */
export const customerOriginSchema = z.enum(['famesystems', 'manual', 'altsystem', 'vorsystem']);
/**
 * Die Herkünfte, die eine **Anlage** wählen darf.
 *
 * `altsystem` und `vorsystem` fehlen hier mit Absicht: Die Herkunft sagt aus,
 * woher ein Kundensatz stammt, und das kann nur der Altimport bzw. der
 * Stammdatenlauf der Übernahme wissen (für `vorsystem` bindet die Datenbank
 * die Herkunft zusätzlich an die Quelle, `customer_vorsystem_herkunft`, `0252`). Ein Anlageweg, der sie wählen
 * dürfte, machte aus einer Herkunftsangabe eine Behauptung — und
 * `customer_display_follows_external` (`0014`) bindet die angezeigte Nummer nur
 * bei `famesystems`, sodass ein von Hand gesetztes `altsystem` still an der
 * Kopplung vorbeikäme.
 *
 * Eine eigene Aufzählung und **nicht** `customerOriginSchema.exclude([...])`:
 * Die zwei Wertesätze beantworten zwei verschiedene Fragen („welche Werte gibt
 * es" und „welche darf man wählen"), und eine Ableitung liesse den zweiten still
 * mitwachsen, sobald der erste wächst.
 */
export const customerAnlageOriginSchema = z.enum(['famesystems', 'manual']);
/** Geschäfts- oder Privatkunde. */
export const customerTypeSchema = z.enum(['b2b', 'b2c']);
/**
 * Angegeben oder abgeleitet — die Herkunft eines aus zwei Werten bestehenden
 * Feldes wie `customer_type_source` oder `document.tax_scenario_source`.
 *
 * **Ein Name für zwei Spalten, weil beide dieselbe Frage beantworten**: „hat
 * ein Mensch das gesagt, oder hat das System es vermutet". Zwei getrennte
 * Aufzählungen mit denselben zwei Werten wären zwei Auslegungen derselben
 * Unterscheidung.
 */
export const provenanceSchema = z.enum(['declared', 'derived']);
/**
 * Der Stand einer Verknüpfung zweier Kundensätze (`customer_link.status`).
 *
 * **Drei Werte im Lesemodell, zwei in der Eingabe.** `candidate` setzt das
 * System, nie ein Mensch — `customerLinkInputSchema` und
 * `duplicateDecisionSchema` lassen ihn deshalb bewusst aus. Diese Aufzählung
 * ist die dritte, vollständige Fassung für Antworten, die auch den
 * Systemvorschlag zeigen.
 */
export const linkStatusSchema = z.enum(['candidate', 'duplicate', 'distinct']);
/** Wer eine Verknüpfung erklärt hat — das System oder ein Mensch. */
export const detectedBySchema = z.enum(['system', 'user']);
/** Aktiv oder stillgelegt — der Stand eines Kunden- oder Kreditorensatzes. */
export const activeStatusSchema = z.enum(['active', 'inactive']);
/**
 * Der Stand einer USt-IdNr.-Prüfung.
 *
 * `bzst_manual`/`bzst_api`/`mias_api` waren bis hierher zweimal wortgleich
 * hingeschrieben — hier und in `eingangsrechnungen.ts`. Zwei Abschriften
 * derselben Aufzählung sind zwei Gelegenheiten, dass ein dritter Wert nur an
 * einer davon ankommt.
 */
export const vatCheckMethodSchema = z.enum(['bzst_manual', 'bzst_api', 'mias_api']);
/** Das Ergebnis einer USt-IdNr.-Prüfung. */
export const vatCheckResultSchema = z.enum(['valid', 'invalid', 'unavailable']);
/**
 * Das Objektschema der Kundenanlage, **vor** der Anredenauftrennung.
 *
 * Getrennt gehalten, damit der Controller die Anlageoptionen (`linkTo`,
 * `markDistinct`) danebenstellen kann, ohne `.strict()` aufzugeben.
 */
const customerInputObject = z
    .object({
    origin: customerAnlageOriginSchema,
    externalId: optionalText,
    displayCustomerNo: optionalText,
    legalName: pflichtText('legalName'),
    contactName: optionalText,
    salutation: optionalText,
    email: optionalEmail,
    phone: optionalText,
    street: pflichtText('street'),
    addressExtra: optionalText,
    postalCode: pflichtText('postalCode'),
    city: pflichtText('city'),
    country: pflichtText('country'),
    vatId: optionalVatId,
    customerType: customerTypeSchema,
    sourceSystem: pflichtText('sourceSystem'),
    sourceReference: optionalText,
})
    .strict();
/**
 * Ein Kundensatz, wie ihn `CustomerService.create()` entgegennimmt.
 *
 * `.strict()`: Ein unbekannter Schlüssel ist kein Zusatz, den man übergehen
 * kann, sondern in aller Regel ein Tippfehler in einem Feldnamen — und der
 * bliebe sonst still.
 */
export const customerInputSchema = customerInputObject.transform((wert, ctx) => {
    // Bei Webkunden vergibt FameSystems die Nummer; sie kommt als externalId.
    // Eine zusätzlich angegebene displayCustomerNo wäre entweder dieselbe Zahl
    // ein zweites Mal oder ein Widerspruch — beides ist kein Feld wert.
    if (wert.origin === 'famesystems' && wert.displayCustomerNo !== null) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['displayCustomerNo'],
            message: 'Bei origin famesystems vergibt dieses System die Nummer; sie kommt als externalId',
        });
        return z.NEVER;
    }
    if (wert.origin === 'famesystems' && wert.externalId === null) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['externalId'],
            params: { code: 'CUSTOMER_EXTERNAL_ID_REQUIRED' },
            message: 'Bei origin famesystems ist externalId Pflicht',
        });
        return z.NEVER;
    }
    // Die Anrede steht im Altbestand im Namensfeld und kommt über die Maske
    // ebenso herein. Abgetrennt wird nur, wenn nicht schon eine dasteht.
    if (wert.salutation !== null)
        return wert;
    const geteilt = splitSalutation(wert.legalName);
    if (geteilt.salutation === null)
        return wert;
    if (geteilt.name === null) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['legalName'],
            message: 'Nach dem Abtrennen der Anrede bleibt kein Name übrig',
        });
        return z.NEVER;
    }
    return { ...wert, salutation: geteilt.salutation, legalName: geteilt.name };
});
/**
 * Was sich an einem Kunden ändern lässt — und die Aufzählung **ist** die
 * Durchsetzung, nicht ihre Beschreibung.
 *
 * `origin`, `externalId`, `displayCustomerNo` und `debtorNo` kommen hier nicht
 * vor; `.strict()` lehnt sie ab, und der Trigger `assert_customer_immutables`
 * fängt zusätzlich alles, was am Schema vorbeikommt.
 */
const customerPatchObject = z
    .object({
    legalName: pflichtPatchText('legalName'),
    contactName: optionalPatchText,
    salutation: optionalPatchText,
    email: optionalPatchEmail,
    phone: optionalPatchText,
    street: pflichtPatchText('street'),
    addressExtra: optionalPatchText,
    postalCode: pflichtPatchText('postalCode'),
    city: pflichtPatchText('city'),
    country: pflichtPatchText('country'),
    vatId: optionalPatchVatId,
    customerType: customerTypeSchema.optional(),
    sourceReference: optionalPatchText,
})
    .strict();
/**
 * Der Änderungssatz eines Kunden.
 *
 * Ein gesetztes `customerType` zieht `customerTypeSource = 'declared'` nach
 * sich: Wer den Typ von Hand ändert, macht aus einer Vermutung eine Angabe,
 * und das gehört vermerkt (Spec 3.5, 6.2). Umgesetzt als `.transform()` auf
 * dem Objektschema und nicht als zweites Feld in der Eingabe — ein Feld
 * könnte der Aufrufer widersprüchlich belegen.
 */
export const customerPatchSchema = customerPatchObject.transform((wert) => wert.customerType === undefined ? wert : { ...wert, customerTypeSource: 'declared' });
/**
 * Die Anlageoptionen der Kundenanlage.
 *
 * Sie stehen neben der Eingabe und nicht in ihr: `linkTo` und `markDistinct`
 * beschreiben nicht den Kunden, sondern was der Anwender über die gemeldeten
 * Kandidaten entschieden hat.
 */
export const customerCreateOptionsSchema = z
    .object({
    linkTo: z.array(uuid).optional(),
    markDistinct: z.array(uuid).optional(),
})
    .strict();
// --- Kreditoren -----------------------------------------------------------
/**
 * Der erwartete Rhythmus eines Kreditors (`vendor.expected_cadence`, `0043`)
 * — Grundlage des Lückenberichts
 * (`apps/api/src/eingangsrechnungen/gaps.report.ts`, `E-264`).
 *
 * **VIER Werte und nicht drei.** Anders als `expectedCadenceSchema`
 * (`eingangsrechnungen.ts`), die nur die drei GEMELDETEN Rhythmen kennt, trägt
 * diese Aufzählung zusätzlich `irregular` — die Vorgabe der Spalte
 * (`NOT NULL DEFAULT 'irregular'`) und der sichere Zustand für jeden
 * Kreditor, über den niemand eine Erwartung geäussert hat. Die zwei
 * Aufzählungen sind ABSICHTLICH getrennt und nicht dieselbe mit einem
 * optionalen vierten Wert: `LueckenService.bericht()` filtert `irregular`
 * heraus, bevor eine `Lueckenmeldung` entsteht — dort ist `irregular` kein
 * gültiger Wert des Ergebnisses, hier schon der Vorgabewert der Eingabe.
 */
export const vendorCadenceSchema = z.enum(['monthly', 'quarterly', 'yearly', 'irregular']);
/**
 * Der Rhythmus-VORSCHLAG bei der Anlage. Fehlt er, gilt `irregular` — dieselbe
 * Vorgabe wie die Spalte selbst, hier verdoppelt: `insertVendorRow`
 * (`apps/api/src/vendors/vendor.query.ts`) schreibt das Feld mit einer festen
 * Parameterliste ins INSERT, und ohne einen Wert löste das kein `DEFAULT` der
 * Spalte aus, sondern ein `NULL` gegen eine `NOT NULL`-Spalte.
 */
const vendorCadenceInput = vendorCadenceSchema.default('irregular');
/**
 * Dasselbe als Patch-Feld — OHNE Entfernen-Zustand. Die Spalte ist
 * `NOT NULL`; ein `PATCH { expectedCadence: null }` gibt es nicht zu senden,
 * weil `irregular` selbst bereits der leere Zustand ist (`0043`: „keine
 * Erwartung, keine Meldung").
 */
const vendorCadencePatch = vendorCadenceSchema.optional();
/**
 * Das Zahlungsziel-VORSCHLAGSFELD eines Kreditors in Tagen
 * (`vendor.default_due_days`, `0043`).
 *
 * **Zeichenkette und kein `z.number()`.** `VendorForm.tsx` registriert dieses
 * Feld wie `defaultAccountNumber` über `form.register()`, und
 * `react-hook-form` liefert dort ausnahmslos Zeichenketten; ein `z.number()`
 * müsste die Maske zusätzlich mit `valueAsNumber` registrieren, und kein
 * anderes Feld dieses Formulars tut das. Leer wird `NULL` — „keine
 * Vorbelegung", nicht „null Tage". Keine Untergrenze aus einem Constraint:
 * `0043` legt keine an; eine nichtnegative ganze Zahl ist die einzig
 * plausible Form, und diese Prüfung liefert die verständliche Meldung, bevor
 * eine unplausible Zahl in die Erfassungsmaske vorbelegt würde. Kein Betrag
 * (Hausregel 1 gilt für Geld, nicht für eine Tageszahl) — deshalb `number`
 * und nicht `Money`.
 */
const optionalDueDays = z
    .string()
    .optional()
    .nullable()
    .transform((wert, ctx) => {
    if (wert === undefined || wert === null)
        return null;
    const gestrafft = wert.trim();
    if (gestrafft === '')
        return null;
    const zahl = Number(gestrafft);
    if (!Number.isInteger(zahl) || zahl < 0) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'Das Zahlungsziel muss eine nichtnegative ganze Zahl von Tagen sein',
        });
        return z.NEVER;
    }
    return zahl;
});
/**
 * Dasselbe als Patch-Feld: fehlt es, bleibt es; `null` oder `''` heisst
 * „Vorbelegung entfernen" — dieselbe Lesart wie bei `optionalPatchText`.
 */
const optionalPatchDueDays = z
    .union([z.string(), z.null()])
    .optional()
    .transform((wert, ctx) => {
    if (wert === undefined)
        return undefined;
    if (wert === null)
        return null;
    const gestrafft = wert.trim();
    if (gestrafft === '')
        return null;
    const zahl = Number(gestrafft);
    if (!Number.isInteger(zahl) || zahl < 0) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'Das Zahlungsziel muss eine nichtnegative ganze Zahl von Tagen sein',
        });
        return z.NEVER;
    }
    return zahl;
});
/** Ein Kreditorensatz, wie ihn `VendorService.create()` entgegennimmt. */
export const vendorInputSchema = z
    .object({
    legalName: pflichtText('legalName'),
    contactName: optionalText,
    email: optionalEmail,
    street: pflichtText('street'),
    addressExtra: optionalText,
    postalCode: pflichtText('postalCode'),
    city: pflichtText('city'),
    country: pflichtText('country'),
    vatId: optionalVatId,
    iban: optionalIban,
    defaultAccountNumber: optionalText,
    defaultTaxRegime: optionalTaxRegime,
    expectedCadence: vendorCadenceInput,
    defaultDueDays: optionalDueDays,
    sourceSystem: pflichtText('sourceSystem'),
    sourceReference: optionalText,
})
    .strict();
/** Der Änderungssatz eines Kreditors. `creditorNo` kommt nicht vor. */
export const vendorPatchSchema = z
    .object({
    legalName: pflichtPatchText('legalName'),
    contactName: optionalPatchText,
    email: optionalPatchEmail,
    street: pflichtPatchText('street'),
    addressExtra: optionalPatchText,
    postalCode: pflichtPatchText('postalCode'),
    city: pflichtPatchText('city'),
    country: pflichtPatchText('country'),
    vatId: optionalPatchVatId,
    iban: optionalPatchIban,
    defaultAccountNumber: optionalPatchText,
    defaultTaxRegime: optionalPatchTaxRegime,
    expectedCadence: vendorCadencePatch,
    defaultDueDays: optionalPatchDueDays,
    sourceReference: optionalPatchText,
})
    .strict();
/** Die Anlageoption der Kreditorenanlage. */
export const vendorCreateOptionsSchema = z
    .object({ acknowledgedSimilar: z.boolean().optional() })
    .strict();
// --- Import ---------------------------------------------------------------
/**
 * Ein Satz des Bestandsimports.
 *
 * Bewusst **grosszügiger** als `customerInputSchema`: Der Import lehnt Sätze
 * nicht ab, er berichtet über sie. `billType` kommt als Rohwert herein, weil
 * ein unbekannter Wert `IMPORT_BILL_TYPE_UNKNOWN` ergeben muss und nicht
 * `VALIDATION_FAILED`; Strasse, PLZ und Ort sind hier optional, weil die
 * Ablehnung in Schritt 3 des Ablaufs steht und damit **hinter** der Auflösung
 * von `billType` und Land (Spec 6.6).
 */
export const customerImportRowSchema = z
    .object({
    /**
     * `1` → `manual`, `2` → `famesystems`, `3` → `altsystem`. Jeder andere
     * Wert wird abgelehnt.
     *
     * **`3` seit `CU-kundenzubringer` (2026-09-03).** `customer_origin` führt
     * den dritten Wert seit `0120`, und `0139` bindet ihn an dieselbe
     * Nummernpflicht wie `famesystems`; ohne diesen Rohwert bliebe der einzige
     * Weg in den dritten Herkunftsraum der Rechnungs-Altimport, und der
     * Kundenstamm des Altsystems (`fa_kundenstammdaten`) käme gar nicht an.
     */
    billType: z.union([z.number(), z.string()]),
    externalId: optionalText,
    companyName: optionalText,
    name: optionalText,
    ustId: importVatId,
    street: optionalText,
    addressExtra: optionalText,
    postalCode: optionalText,
    city: optionalText,
    country: optionalText,
    email: optionalEmail,
    phone: optionalText,
    sourceSystem: z.string().optional(),
    sourceReference: optionalText,
    /** Zeitstempel der Quellzeile — entscheidet nach A-4, welche Kopie gilt. */
    createdDate: optionalText,
})
    .strict();
/** Die Anfrage des Importendpunkts. */
export const customerImportRequestSchema = z
    .object({ rows: z.array(customerImportRowSchema) })
    .strict();
// --- Verknüpfungen und Prüfprotokoll --------------------------------------
/**
 * Eine von Hand erklärte Verknüpfung.
 *
 * `status` kennt `candidate` nicht: Ein Mensch, der einen Zusammenhang
 * erklärt, hat bereits entschieden (Spec 6.4).
 *
 * `note` ist hier `optionalText` und **nicht** ein Pflichtfeld. Das ist
 * Absicht: Die Pflicht steht als `customer_link_reason` in der Datenbank, und
 * eine Zod-Pflicht davor nähme dem Constraint seinen einzigen erreichbaren
 * Weg — der Aufrufer bekäme `VALIDATION_FAILED` statt `CUSTOMER_LINK_INVALID`.
 */
export const customerLinkInputSchema = z
    .object({
    linkedCustomerId: uuid,
    status: z.enum(['duplicate', 'distinct']),
    note: optionalText,
})
    .strict();
/** Die Entscheidung über einen gemeldeten Kandidaten. */
export const duplicateDecisionSchema = z
    .object({ status: z.enum(['duplicate', 'distinct']) })
    .strict();
/**
 * Die Freigabe einer Verknüpfung.
 *
 * `reason` ist aus demselben Grund `optionalText` wie `note` oben: Die Pflicht
 * trägt `customer_link_release`.
 */
export const linkReleaseSchema = z.object({ reason: optionalText }).strict();
/** Die Aufzeichnung einer von Hand durchgeführten USt-IdNr.-Prüfung. */
export const vatCheckInputSchema = z
    .object({
    /** Zeitpunkt der Abfrage. `vat_id_check_not_future` verlangt `<= recorded_at`. */
    checkedAt: z.string().datetime({ offset: true }),
    method: vatCheckMethodSchema,
    result: vatCheckResultSchema,
    authorityReference: optionalText,
    responsePayload: z.record(z.unknown()).nullable().optional(),
    nameConfirmed: z.boolean().nullable().optional(),
    addressConfirmed: z.boolean().nullable().optional(),
})
    .strict();
// --- Antworten --------------------------------------------------------------
//
// Auftrag `V-vertrag2` (2026-08-29): Die dreizehn [UNGEKLÄRT]-Marken bei
// customers und die acht bei vendors kamen NICHT aus fehlenden Verträgen für
// die Eingabe, sondern aus fehlenden für die AUSGABE — `CustomerView`,
// `VendorView`, `LinkView`, `DuplicateEntry`, `VatCheckView` und
// `ImportReport` waren reine `interface`-Deklarationen in
// `apps/api/src/customers/*.ts`, `apps/api/src/vat/vat-check.service.ts` und
// `apps/api/src/vendors/vendor.query.ts`, ohne Gegenstück hier. Namensregel
// wie überall in diesem Paket und wie in `bewegungsstandAntwortSchema`
// (`packages/contracts/src/kontierung.ts`, Handbuch 3.359/3.360): Der
// Rückgabetyp `Promise<X>` im Controller und der Vertragsname `xSchema`
// müssen nach `schemaNameZuTyp` (`apps/api/src/openapi/aufloesung.ts`)
// zusammenpassen, sonst bleibt die Marke stehen, auch wenn ein Schema mit
// anderem Namen existiert.
/**
 * Eine offene Verknüpfung, aus der Sicht EINES Kunden — `CustomerLinkView`
 * (`apps/api/src/customers/customer.query.ts`), eingebettet in
 * `CustomerView.openLinks`.
 */
export const customerLinkViewSchema = z
    .object({
    linkId: uuid,
    otherCustomerId: uuid,
    otherDisplayCustomerNo: z.string(),
    status: linkStatusSchema,
    detectedBy: detectedBySchema,
    matchedCriteria: z.array(z.string()).readonly(),
})
    .strict();
/**
 * Der Stand der USt-IdNr.-Prüfung, wie er an einem Kunden ODER einem Kreditor
 * hängt — dieselben vier Felder, wortgleich in `CustomerView.vatStatus`
 * (`apps/api/src/customers/customer.query.ts`) und `VendorView.vatStatus`
 * (`apps/api/src/vendors/vendor.query.ts`). Nicht exportiert: Es ist kein
 * eigener Rückgabetyp, sondern nur an diesen beiden Stellen eingebettet.
 */
const vatStatusViewSchema = z
    .object({
    checkedAt: z.string().nullable(),
    result: vatCheckResultSchema.nullable(),
    method: vatCheckMethodSchema.nullable(),
    matchesCurrentVatId: z.boolean(),
})
    .strict();
/**
 * Ein Kundensatz, wie er nach aussen geht — `CustomerView`
 * (`apps/api/src/customers/customer.query.ts`, `toView()`). Antwortform von
 * `GET /customers`, `GET /customers/{id}`, `POST /customers`,
 * `PATCH /customers/{id}` und den beiden Aktivierungsrouten.
 */
export const customerViewSchema = z
    .object({
    id: uuid,
    origin: customerOriginSchema,
    externalId: z.string().nullable(),
    displayCustomerNo: z.string(),
    debtorNo: z.string(),
    legalName: z.string(),
    contactName: z.string().nullable(),
    salutation: z.string().nullable(),
    email: z.string().nullable(),
    phone: z.string().nullable(),
    street: z.string(),
    addressExtra: z.string().nullable(),
    postalCode: z.string(),
    city: z.string(),
    countryCode: z.string(),
    vatId: z.string().nullable(),
    vatStatus: vatStatusViewSchema,
    customerType: customerTypeSchema,
    customerTypeSource: provenanceSchema,
    status: activeStatusSchema,
    sourceSystem: z.string(),
    sourceReference: z.string().nullable(),
    openLinks: z.array(customerLinkViewSchema).readonly(),
    createdAt: z.string(),
    updatedAt: z.string(),
})
    .strict();
/**
 * Die Antworthülle von `GET /api/v1/customers` — `{ customers: [...] }`. Der Umschlag lebt neben seinem
 * Elementschema, damit Server und Oberfläche dieselbe Hülle prüfen (Handbuch §3.969).
 */
export const customerListResponseSchema = z.object({ customers: z.array(customerViewSchema).readonly() }).strict();
/**
 * Eine Verknüpfung, wie sie nach aussen geht — `LinkView`
 * (`apps/api/src/customers/duplicates.service.ts`, `toView()`). Antwortform
 * von `POST /customers/duplicates/{id}/decide`,
 * `POST /customers/duplicates/{id}/release` und `POST /customers/{id}/links`.
 */
export const linkViewSchema = z
    .object({
    linkId: uuid,
    customerId: uuid,
    linkedCustomerId: uuid,
    status: linkStatusSchema,
    detectedBy: detectedBySchema,
    matchedCriteria: z.array(z.string()).readonly(),
    note: z.string().nullable(),
    decidedAt: z.string().nullable(),
    decidedBy: z.string().nullable(),
    releasedAt: z.string().nullable(),
    releaseReason: z.string().nullable(),
    createdAt: z.string(),
})
    .strict();
/**
 * Ein Eintrag des Dubletten-Arbeitsvorrats — `DuplicateEntry`
 * (`apps/api/src/customers/duplicates.service.ts`), Antwortform von
 * `GET /customers/duplicates`. Trägt **beide** Kundensätze vollständig
 * (`left`, `right`), flach neben den `LinkView`-Feldern — genau wie der
 * Dienst ihn baut (`{ ...toView(row), left, right }`), deshalb `.extend()`
 * und keine Verschachtelung.
 */
export const duplicateEntrySchema = linkViewSchema
    .extend({
    left: customerViewSchema,
    right: customerViewSchema,
})
    .strict();
/**
 * Die Antworthülle von `GET /api/v1/customers/duplicates` — `{ candidates: [...] }`. Der Umschlag lebt neben seinem
 * Elementschema, damit Server und Oberfläche dieselbe Hülle prüfen (Handbuch §3.969).
 */
export const duplicateListResponseSchema = z.object({ candidates: z.array(duplicateEntrySchema).readonly() }).strict();
/**
 * Eine aufgezeichnete USt-IdNr.-Prüfung, wie sie nach aussen geht —
 * `VatCheckView` (`apps/api/src/vat/vat-check.service.ts`). EIN Vertrag für
 * BEIDE Parteien: Der Dienst unterscheidet Kunde/Kreditor nur über
 * `VatParty` beim Aufruf, nicht im Rückgabetyp. Antwortform von
 * `POST /customers/{id}/vat-check`, `GET /customers/{id}/vat-checks`,
 * `POST /vendors/{id}/vat-check` und `GET /vendors/{id}/vat-checks`.
 */
export const vatCheckViewSchema = z
    .object({
    id: uuid,
    vatId: z.string(),
    checkedAt: z.string(),
    method: vatCheckMethodSchema,
    result: vatCheckResultSchema,
    authorityReference: z.string().nullable(),
    nameConfirmed: z.boolean().nullable(),
    addressConfirmed: z.boolean().nullable(),
    recordedAt: z.string(),
    recordedBy: z.string(),
})
    .strict();
/**
 * Die Antworthülle von `GET /api/v1/customers/:id/vat-checks` und `GET /api/v1/vendors/:id/vat-checks` — `{ checks: [...] }`. Der Umschlag lebt neben seinem
 * Elementschema, damit Server und Oberfläche dieselbe Hülle prüfen (Handbuch §3.969).
 */
export const vatCheckListResponseSchema = z.object({ checks: z.array(vatCheckViewSchema).readonly() }).strict();
/**
 * Ein Kreditorensatz, wie er nach aussen geht — `VendorView`
 * (`apps/api/src/vendors/vendor.query.ts`, `toView()`). Antwortform von
 * `GET /vendors`, `GET /vendors/{id}`, `POST /vendors`,
 * `PATCH /vendors/{id}` und den beiden Aktivierungsrouten.
 *
 * **`legalName` ist der LEBENDE Name aus `vendor.legal_name`, keine
 * eingefrorene Kopie.** Anders als der ausgehende Beleg
 * (`document.billing_legal_name`), die Bankbewegung
 * (`bank_transaction.counterparty_name`) oder — seit Migration `0067` und,
 * für die Anzeige, seit Owner-Entscheid 2026-09-01 (`D-listennamen`,
 * `E-754`) — die Eingangsrechnung (`incoming_invoice.vendor_legal_name`)
 * friert hier nichts ein: Der Kreditorensatz selbst IST die
 * Stammdatenquelle, es gibt keine zweite Kopie, die auseinanderlaufen
 * könnte. Handbuch §3.204/§3.468 hält fest, warum das für den Kreditor
 * selbst richtig bleibt, obwohl seine Belege längst einfrieren: Die
 * Indexzeile bzw. Listenzeile **eines Belegs** darf nicht auf heutige
 * Stammdaten zeigen, die Zeile **des Kreditors selbst** ist keine
 * Belegzeile — sie ist die Quelle.
 */
export const vendorViewSchema = z
    .object({
    id: uuid,
    creditorNo: z.string(),
    legalName: z.string(),
    contactName: z.string().nullable(),
    email: z.string().nullable(),
    street: z.string(),
    addressExtra: z.string().nullable(),
    postalCode: z.string(),
    city: z.string(),
    countryCode: z.string(),
    vatId: z.string().nullable(),
    iban: z.string().nullable(),
    defaultAccountNumber: z.string().nullable(),
    /**
     * Die Vorschlagskontonummer samt der Angabe, ob es zu ihr HEUTE eine
     * gültige Version gibt — wortgleich zu `VendorView.defaultAccount` im
     * Dienst, Begründung dort (`vendor.query.ts`): `validToday` ist reine
     * Auskunft für die Maske, kein Constraint prüft es.
     */
    defaultAccount: z.object({ number: z.string(), validToday: z.boolean() }).strict().nullable(),
    /**
     * Das Steuerregime-VORSCHLAGSFELD — VORSCHLAG für die Erfassungsmaske,
     * KEINE Zusage über eine Buchung (`vendor.default_tax_regime`, `0043`).
     * `null` heisst „kein Vorschlag" und nicht „regular".
     */
    defaultTaxRegime: incomingTaxRegimeSchema.nullable(),
    /**
     * Der erwartete Rhythmus (`vendor.expected_cadence`, `0043`) — Grundlage
     * des Lückenberichts (`gaps.report.ts`, `E-264`). `irregular` heisst
     * „keine Erwartung, keine Meldung" und ist die Vorgabe für jeden
     * Kreditor, über den niemand eine Erwartung geäussert hat. Anders als
     * `defaultTaxRegime` und `defaultDueDays` nicht `.nullable()`: Die Spalte
     * ist `NOT NULL`, und `irregular` ist bereits ihr leerer Zustand.
     */
    expectedCadence: vendorCadenceSchema,
    /**
     * Das Zahlungsziel-VORSCHLAGSFELD in Tagen (`vendor.default_due_days`,
     * `0043`) — VORSCHLAG für die Erfassungsmaske, KEINE Zusicherung
     * (`0043`: „eine Bequemlichkeit und keine Zusicherung: Zahlungsverkehr
     * gibt es in diesem System nicht"). `null` heisst „keine Vorbelegung".
     */
    defaultDueDays: z.number().int().nullable(),
    vatStatus: vatStatusViewSchema,
    status: activeStatusSchema,
    sourceSystem: z.string(),
    sourceReference: z.string().nullable(),
    createdAt: z.string(),
    updatedAt: z.string(),
})
    .strict();
/**
 * Die Antworthülle von `GET /api/v1/vendors` — `{ vendors: [...] }`. Der Umschlag lebt neben seinem
 * Elementschema, damit Server und Oberfläche dieselbe Hülle prüfen (Handbuch §3.969).
 */
export const vendorListResponseSchema = z.object({ vendors: z.array(vendorViewSchema).readonly() }).strict();
/**
 * Ein Grund im Prüfbericht des Kundenimports — `ImportIssue`
 * (`apps/api/src/customers/import.service.ts`).
 *
 * **`code` bleibt `z.string()` und wird NICHT als Aufzählung nachgebaut.**
 * Der wirkliche Typ ist `ErrorCode | 'POSTAL_CODE_IMPLAUSIBLE' |
 * 'IMPORT_VALUE_CONFLICT'`; `ErrorCode` allein zählt über hundert Werte in
 * `apps/api/src/common/error-codes.ts` (`keyof typeof ERROR_STATUS`), einer
 * Datei ausserhalb dieses Pakets. Eine Abschrift hier liefe der Sollliste
 * dort unbemerkt hinterher — derselbe Grund, aus dem
 * `fintsAbrufAntwortSchema.fehler.code` (`packages/contracts/src/fints.ts`)
 * und die feste Fehlerform in `apps/api/src/openapi/dokument.ts`
 * (`FEHLERANTWORTEN.Fehler`) `code` ebenfalls als Zeichenkette führen.
 */
export const importIssueSchema = z
    .object({
    code: z.string(),
    field: z.string().optional(),
    message: z.string(),
})
    .strict();
/**
 * Das Ergebnis EINES Satzes des Kundenimports — `ImportRowResult`
 * (`apps/api/src/customers/import.service.ts`).
 */
export const importRowResultSchema = z
    .object({
    externalId: z.string().nullable(),
    origin: customerOriginSchema.nullable(),
    created: z.boolean(),
    rejected: z.boolean(),
    customerId: z.string().nullable(),
    reasons: z.array(importIssueSchema).readonly(),
    warnings: z.array(importIssueSchema).readonly(),
})
    .strict();
/**
 * Der Prüfbericht — die Antwort von `POST /api/v1/customers/import`
 * (`ImportReport`, `apps/api/src/customers/import.service.ts`).
 */
export const importReportSchema = z
    .object({ rows: z.array(importRowResultSchema).readonly() })
    .strict();
/* -------------------------------------------------------------------------
 * Die Kundenakte — `GET /api/v1/customers/{id}/akte`, Auftrag `K-kundenakte`
 * (2026-09-09), Owner-Entscheid vom 2026-09-08 (`D-31` der Altimport-Spec:
 * «Überall. Die Altrechnungen sind ja genauso Teil unserer Buchhaltung.»)
 * ---------------------------------------------------------------------- */
/**
 * Der Umsatz eines Kalenderjahres — Netto, Steuer, Brutto.
 *
 * **Dieselbe Ableitung wie die Startkennzahlen, nicht eine zweite.** Die drei
 * Beträge kommen aus `summeUmsatzUndGutschrift()`
 * (`apps/api/src/berichte/startkennzahlen.report.ts`), demselben Aufruf, den
 * die Startseite und das Dashboard benutzen — nur mit einem Kundenfilter und
 * einer Jahresspanne. Eine eigene Summierung je Kunde wäre genau der zweite
 * Weg zu derselben Zahl, den jener Bericht seit `B-dashagg` (2026-08-31)
 * ausdrücklich vermeidet.
 *
 * **Was daraus folgt, und es ist die tragende Aussage dieser Akte:** Es zählt,
 * was DIESES System gebucht hat. Eine gebuchte Altrechnung des Jahres 2026
 * (`legacy_invoice` mit gesetztem `document.journal_entry_id`) steht mit
 * ihrem vollen Betrag hier; eine Altrechnung der ARCHIVKLASSE — vor dem
 * Journalbeginn ausgestellt und nie gebucht — steht NICHT hier, weil ihr
 * Erlös im Altsystem gebucht ist und ein zweites Mal gezählt doppelt wäre
 * (`E-1062`/`E-1063`). Beide erscheinen trotzdem in der Belegliste der
 * Kundenseite: Die Frage «welche Belege hat dieser Kunde» ist eine andere als
 * «welchen Umsatz hat dieses System für ihn gebucht».
 */
export const kundenakteUmsatzJahrSchema = z
    .object({
    /** Das Kalenderjahr des Ausstellungsdatums. */
    jahr: z.number().int(),
    netTotal: centAmount,
    taxTotal: centAmount,
    grossTotal: centAmount,
})
    .strict();
/**
 * Die offenen Posten EINES Kunden.
 *
 * **Dieselbe Auflösung wie die Offene-Posten-Seite, nicht eine zweite.** Die
 * Zahlen kommen aus `OffenePostenQuery` und damit aus der Sicht
 * `beleg_offener_rest` (Migration `0161`, fortgeschrieben von `0174`) — mit
 * denselben beiden Bedingungen, die dort die Seite tragen:
 * `forderung_gebucht` (dieses System führt die Forderung im Journal) und
 * `NOT gilt_als_erledigt` (die Fünf-Cent-Grenze aus `D-29`, in `0161`
 * gerechnet und nirgends sonst).
 *
 * **`ueberfaellig*` benutzt dieselbe Grenze wie die Offene-Posten-Seite:**
 * ein gesetztes Zahlungsziel, das VOR dem Stichtag liegt. Ein Beleg ohne
 * Zahlungsziel ist nie überfällig, und ein Beleg, an dem nichts mehr offen
 * ist, steht ohnehin nicht in der Sicht.
 */
export const kundenakteOffenePostenSchema = z
    .object({
    /** Die Summe der offenen Reste dieses Kunden. */
    summe: centAmount,
    /** Wie viele Belege dieses Kunden einen offenen Rest tragen. */
    anzahl: z.number().int().nonnegative(),
    /** Die Summe der offenen Reste, deren Zahlungsziel überschritten ist. */
    ueberfaelligSumme: centAmount,
    /** Wie viele davon ihr Zahlungsziel überschritten haben. */
    ueberfaelligAnzahl: z.number().int().nonnegative(),
    /**
     * Das Ausstellungsdatum des ältesten offenen Belegs, oder `null`.
     *
     * **Nur das Datum und nicht der ganze Beleg** — anders als
     * `OffenePostenKunde.aelteste`, das eine vollständige Belegzeile trägt.
     * Die Kundenseite zeigt die Belege ohnehin darunter in ihrer eigenen
     * Liste; den Beleg hier ein zweites Mal mitzuschicken wäre dieselbe
     * Angabe an zwei Stellen, und die zweite veraltet.
     */
    aeltestesDatum: isoDate.nullable(),
})
    .strict();
/**
 * Die Kundenakte — Antwortform von `GET /api/v1/customers/{id}/akte`.
 *
 * **Die Belegliste steht bewusst NICHT darin.** Sie kommt über
 * `GET /api/v1/documents?customerId=…` — denselben Endpunkt, den die
 * Belegliste benutzt, mit derselben Paginierung, denselben Filtern und
 * derselben Sortierung. Ein zweiter, eingebetteter Belegweg wäre eine zweite
 * Ableitung derselben Liste, die bei jedem Filter, den `/documents` dazu
 * bekommt, zurückbliebe; und eine Akte, die ihre Belege vollständig
 * mitliefert, wäre bei einem Kunden mit zweihundert Altrechnungen eine
 * Antwort, die niemand lesen will.
 */
export const kundenakteSchema = z
    .object({
    customerId: uuid,
    /** Der Kalendertag, gegen den «überfällig» gerechnet ist (`CLOCK.today()`). */
    stichtag: isoDate,
    /**
     * Ein Eintrag je Kalenderjahr, in dem dieser Kunde gebuchten Umsatz
     * trägt — das jüngste Jahr zuerst. Ein Jahr ohne gebuchten Beleg
     * erscheint gar nicht; eine Zeile mit `0,00` behauptete eine Messung, wo
     * es keine gibt.
     */
    umsatzJahre: z.array(kundenakteUmsatzJahrSchema).readonly(),
    offenePosten: kundenakteOffenePostenSchema,
    /** Was diese Zahlen NICHT sagen — im Klartext, für die Anzeige. */
    grenze: z.string(),
})
    .strict();
//# sourceMappingURL=master-data.js.map